"use client";

import Image from "next/image";
// import { useRootContext } from "@context/RootProvider";
import useSearchParams from "@/hooks/useSearchParams";
import { TypeAdb2cCustomPolicy, TypeTokenResponse } from "@/common/lib/types";
import { isLocalEnv } from "@/runtimeConfig/public";
import { logoutRedirectUrl, sessionQueryParams } from "@/apiUtils/constants";
import { getAccessToken } from "@/api/auth";
import PreloaderGif from "@/assets/gif/preloader.gif";
import { markSocialLoginStart } from "@/utils/socialLoginHistoryUtils";
import { AvailableSocialLogins, useGlobalStore } from "@/store/store";
import {
  mapPosLanguageIdToLang,
  resolveLocaleFromLang,
  rewriteUrlWithLocale,
  setClientLocaleCookie,
} from "@/utils/posPreferredLanguageUtils";
// import { us} from '@/store/store';

import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import useRouter from "@/hooks/useCRouter";
import { useParams, usePathname } from "next/navigation";

interface OIDCFrameProps {
  height?: number | string;
  width?: number | string;
  policy: TypeAdb2cCustomPolicy;
  id?: string;
  htmlKey?: string;
  style?: React.CSSProperties;
  onGuest?: Function;
  onSuccess?: Function;
  show?: boolean;
  customOptions?: Record<string, unknown>;
  buttonLabel?: string;
  /** Profile edit: prefill preferred language from POS and redirect to the submitted language's locale on success. */
  enablePosLanguageBridge?: boolean;
}

const OIDCFrame = forwardRef<HTMLDivElement, OIDCFrameProps>((props, ref) => {
  const {
    height,
    width,
    policy,
    htmlKey: propHtmlKey,
    id,
    style,
    onGuest,
    onSuccess,
    show,
    customOptions,
    buttonLabel,
    enablePosLanguageBridge,
  } = props;

  //   socialProviders = [
  //     "apple.com",
  //     "facebook.com",
  //     "google.com"
  // ];

  // console.log("socialProviders outside", socialProviders);
  const pathname = usePathname();
  const locale = (useParams()?.locale as string) || "";

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const localeRef = useRef(locale);
  const enablePosLanguageBridgeRef = useRef(enablePosLanguageBridge);
  const submittedLanguageRef = useRef<string | null>(null);

  const updateUser = useGlobalStore((state) => state.setUser);
  const setIsAuthenticated = useGlobalStore((state) => state.setIsAuthenticated);
  const socialProviders = useGlobalStore((state) => state.availableSocialLogins);

  //   const { updateUser } = useRootContext();
  const updateUserRef = useRef(updateUser);
  const setIsAuthenticatedRef = useRef(setIsAuthenticated);
  const onSuccessRef = useRef(onSuccess);
  const userJourneyRef = useRef<string>();
  const [hasGuestCheckout, setHasGuestCheckout] = useState(!!onGuest);
  const [htmlKey, setHtmlKey] = useState<string>(propHtmlKey || policy);
  const hethCheckTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const iframeNotYetReadyRef = useRef(false);
  const isLoadingSilent = useRef(false);

  const socialProvidersRef = useRef(socialProviders);

  // const stableSocialProviders = useMemo(() => socialProviders.length ? socialProviders : socialProvidersRef.current, [socialProviders]);

  // console.log("socialProviders inside ",socialProviders);

  const { push } = useRouter();

  const pushRef = useRef(push);
  const { updateSearchParam, searchParams } = useSearchParams();
  const updateSearchParamRef = useRef(updateSearchParam);
  const onGuestRef = useRef(onGuest);
  const [initialSearchParams] = useState(
    searchParams?.get(sessionQueryParams.LOGIN_MODAL_STATUS)
      ? `${sessionQueryParams.LOGIN_MODAL_STATUS}=${searchParams.get(sessionQueryParams.LOGIN_MODAL_STATUS)}`
      : "",
  );

  const getRedirectUrl = useCallback(
    () =>
      (searchParams.has(sessionQueryParams.REDIRECT)
        ? searchParams.get(sessionQueryParams.REDIRECT)
        : encodeURIComponent("/my-account")) as string,
    [searchParams],
  );

  const postToIframeRef = useRef((payload: { [key: string]: string | Object } | string) => {
    iframeRef.current?.contentWindow?.postMessage(payload, "*");
  });

  const redirectUrlRef = useRef(getRedirectUrl());

  const iframeReadyHandlerRef = useRef<Function>(
    (iframeRefCurrent: HTMLIFrameElement, options: { hasGuestCheckout?: boolean }) => {
      iframeRefCurrent?.contentWindow?.postMessage(
        {
          type: "SESSION_FROM",
          payload: { sessionFrom: options?.hasGuestCheckout ? "checkout" : "default" },
        },
        "*",
      );

      if (typeof options?.hasGuestCheckout !== "undefined") {
        iframeRefCurrent?.contentWindow?.postMessage(
          { type: "GUEST_CHECKOUT", payload: { show: options?.hasGuestCheckout } },
          "*",
        );
      }
    },
  );

  useEffect(() => {
    updateUserRef.current = updateUser;
  }, [updateUser]);
  useEffect(() => {
    updateSearchParamRef.current = updateSearchParam;
  }, [updateSearchParam]);
  useEffect(() => {
    redirectUrlRef.current = getRedirectUrl();
  }, [getRedirectUrl]);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);
  useEffect(() => {
    setHasGuestCheckout(!!onGuest);
    onGuestRef.current = onGuest;
  }, [onGuest]);
  useEffect(() => {
    iframeReadyHandlerRef.current(iframeRef.current, { hasGuestCheckout });
  }, [hasGuestCheckout]);
  useEffect(() => {
    pushRef.current = push;
  }, [push]);
  useEffect(() => {
    localeRef.current = locale;
  }, [locale]);
  useEffect(() => {
    enablePosLanguageBridgeRef.current = enablePosLanguageBridge;
  }, [enablePosLanguageBridge]);

  useEffect(() => {
    if (!show) {
      if (
        userJourneyRef.current &&
        ["forgotpasswordstep1", "forgotpasswordstep3"].includes(userJourneyRef.current)
      ) {
        setHtmlKey((prev) => (prev || "") + "_");
      }
    }
  }, [show]);

  useEffect(() => {
    if (!isLocalEnv && show && iframeRef.current?.contentWindow) {
      postToIframeRef.current({ type: "HEALTH_CHECK", payload: {} });

      iframeNotYetReadyRef.current = true;
      if (hethCheckTimeoutRef.current) {
        clearTimeout(hethCheckTimeoutRef.current);
      }
      hethCheckTimeoutRef.current = setTimeout(() => {
        if (iframeNotYetReadyRef.current) {
          setHtmlKey((prev) => (prev || "") + "_");
        }
        if (hethCheckTimeoutRef.current) {
          clearTimeout(hethCheckTimeoutRef.current);
        }
      }, 5000);
    }
    return () => {
      if (hethCheckTimeoutRef.current) {
        clearTimeout(hethCheckTimeoutRef.current);
      }
    };
  }, [show]);

  useEffect(() => {
    if (!Array.isArray(socialProviders)) return;

    // if(!socialProviders) return;
    socialProvidersRef.current = socialProviders ?? [];

    if (show && Array.isArray(socialProviders)) {
      console.log("postingToIframe");
      postToIframeRef.current({
        type: "ALLOWED_SOCIAL_PROVIDERS",
        payload: socialProviders ?? [],
      });
    }
  }, [show, socialProviders]);

  useLayoutEffect(() => {
    const handleSocialLoginButtonClick = async (provider: string) => {
      if (
        typeof window !== "undefined" &&
        socialProvidersRef.current?.includes(provider as AvailableSocialLogins)
      ) {
        const federationUrl =
          `/api/OIDC/federation/${provider}` +
          (redirectUrlRef.current ? `?redirect=${redirectUrlRef.current}` : "");
        // Record the start page so the callback can jump back over the provider's pages (MLC-2674). assign(), not
        // replace(): the marker entry must stay, because the post-login destination replaces it.
        markSocialLoginStart(window);
        window.location.assign(federationUrl);
      }
    };

    const loadAndPostPosPreferredLanguage = async () => {
      try {
        const listResp = await fetch("/api/pos/list", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ locale: localeRef.current }),
        });
        const listJson = await listResp.json().catch(() => ({}));
        const customer = listJson?.data?.result?.[0] ?? listJson?.result?.[0];
        const preferredLanguage = mapPosLanguageIdToLang(customer?.preferedLanguageId ?? customer?.preferedLangId);
        if (preferredLanguage) {
          postToIframeRef.current({ type: "POS_PREFERRED_LANGUAGE", payload: { preferredLanguage } });
        }
      } catch (error) {
        console.error("Failed to load POS preferred language", error);
      }
    };

    /** Points the post-save redirect at the locale of the language submitted in profile edit, if it changed. */
    const applySubmittedLanguageLocale = (redirectUrl?: string) => {
      const submittedLanguage = submittedLanguageRef.current;
      submittedLanguageRef.current = null;

      const currentLocale = localeRef.current;
      const targetLocale = resolveLocaleFromLang(submittedLanguage, currentLocale);
      if (!targetLocale) return redirectUrl;

      setClientLocaleCookie(targetLocale);
      const absoluteUrl = new URL(redirectUrl || window.location.href, window.location.origin).toString();
      return rewriteUrlWithLocale(absoluteUrl, targetLocale);
    };

    const messageHandler = async (event: MessageEvent) => {
      const { data } = event as MessageEvent<{ type: string; payload: unknown }>;
      // Only token-bearing messages must come from our own callback route.
      if (
        (data?.type === "USER_INFO" || data?.type === "REDIRECT") &&
        event.origin !== window.location.origin
      ) {
        return;
      }
      const handleTokenResponse = (response: TypeTokenResponse) => {
        if (typeof window !== "undefined") {
          const { redirectUrl: responseRedirectUrl, ...rest } = response;
          const redirectUrl = enablePosLanguageBridgeRef.current
            ? applySubmittedLanguageLocale(responseRedirectUrl)
            : responseRedirectUrl;
          updateUserRef.current({ ...rest });
          setIsAuthenticatedRef.current(true);

          // Use replace = true to prevent adding login modal URL to browser history
          // This ensures back button returns to cart, not to the login modal URL
          updateSearchParamRef.current(
            [{ key: sessionQueryParams.LOGIN_MODAL_STATUS, value: undefined }],
            true // replace = true to avoid adding history entry
          );

          onSuccessRef.current?.(rest, redirectUrl);
          setHtmlKey((prev) => (prev || "") + "_");
          if (redirectUrl) {
            // Use location.replace() to prevent adding redirect URL to browser history
            window.location.replace(redirectUrl);
          }
        }
      };

      switch (data?.type) {
        case "USER_INFO": {
          if (data?.payload) {
            let response;
            try {
              const { code, state } = data.payload as { code: string; state: string };
              if (code) {
                if (isLoadingSilent.current) {
                  break;
                }
                // replace = true so the login-modal URL entry is overwritten instead of left in history
                updateSearchParamRef.current(
                  [{ key: sessionQueryParams.LOGIN_MODAL_STATUS, value: undefined }],
                  true
                );
                try {
                  isLoadingSilent.current = true;
                  response = await getAccessToken(code, state);
                  if (response ? Object.keys(response).length < 1 : true) {
                    throw new Error("Error while fetching user info");
                  }
                } catch (error) {
                  isLoadingSilent.current = false;
                }
              }
              if (response) {
                handleTokenResponse(response);
              } else {
                throw new Error("Error while fetching user info");
              }
            } catch (error) {
              console.error("Error while fetching user info", error);
            }
          }
          try {
            postToIframeRef.current("close");
          } catch (error) {}
          break;
        }
        case "REDIRECT": {
          const tokenResponse = data.payload as TypeTokenResponse;
          if (tokenResponse) {
            handleTokenResponse(tokenResponse);
          }

          break;
        }
        case "FLOW_CHANGE": {
          const payload = data.payload as { sessionFlow: string };
          updateSearchParamRef.current([
            { key: sessionQueryParams.LOGIN_MODAL_STATUS, value: payload.sessionFlow || undefined },
          ]);
          break;
        }
        case "SOCIAL_PROVIDER": {
          const payload = data.payload as { provider: string };
          if (payload?.provider) {
            handleSocialLoginButtonClick(payload.provider);
          }
          break;
        }
        case "GUEST_CHECKOUT": {
          const payload = data.payload as { email: string };
          if (payload?.email && typeof onGuestRef.current === "function") {
            onGuestRef.current(payload.email);
          }
          break;
        }
        case "READY": {
          iframeNotYetReadyRef.current = false;
          iframeReadyHandlerRef.current(iframeRef.current, {
            hasGuestCheckout: !!onGuestRef.current,
          });
          const payload = data.payload as { userJourney: string; windowHeight: number };
          userJourneyRef.current = payload?.userJourney || "";
          break;
        }
        case "TERMS_CONDITIONS": {
          pushRef.current("/terms-of-use");
          break;
        }
        case "PRIVACY_POLICY": {
          pushRef.current("/privacy-policy");
          break;
        }
        case "HEALTH_CHECK": {
          iframeNotYetReadyRef.current = false;
          break;
        }
        case "LOGOUT": {
          window.location.href = logoutRedirectUrl;
          break;
        }
        case "GET_ALLOWED_SOCIAL_PROVIDERS": {
          if (Array.isArray(socialProvidersRef.current)) {
            postToIframeRef.current({
              type: "ALLOWED_SOCIAL_PROVIDERS",
              payload: socialProvidersRef.current,
            });
          }
          break;
        }
        case "LOAD_LOGIN":
        case "RESET": {
          setHtmlKey((prev) => (prev || "") + "_");
          break;
        }
        case "REQUEST_POS_PREFERRED_LANGUAGE": {
          if (enablePosLanguageBridgeRef.current) {
            await loadAndPostPosPreferredLanguage();
          }
          break;
        }
        case "PROFILE_UPDATE_SUBMITTED": {
          const payload = data.payload as { preferredLanguage?: string };
          if (enablePosLanguageBridgeRef.current && payload?.preferredLanguage) {
            submittedLanguageRef.current = payload.preferredLanguage;
          }
          break;
        }
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("message", messageHandler);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("message", messageHandler);
      }
    };
  }, []);

  const params = new URLSearchParams(initialSearchParams);
  
  // forward utm params (e.g. cart_abandon_email) so iframe can react
  try {
    const utm = searchParams?.get("utm_source");
    if (utm) params.set("utm_source", utm);
  } catch (e) {}

  if (redirectUrlRef.current) {
    params.set(sessionQueryParams.REDIRECT, redirectUrlRef.current);
  }
  if (customOptions) {
    Object.entries(customOptions).forEach(([key, value]) => {
      params.set(key, value as string);
    });
  }
  const src = `/api/OIDC/${policy}?${params.toString()}`;

  return (
    <div className="relative overflow-hidden" style={{ height }} ref={ref}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <span>
          <Image src={PreloaderGif} width={100} height={100} alt="Loading..." />
        </span>
      </div>
      {isLocalEnv && true && false ? (
        <a
          href={src}
          className="absolute rounded bg-blue-500 px-4 py-2 text-white"
          style={{ zIndex: 1, right: 0 }}
        >
          {buttonLabel || "Login / Register"}
        </a>
      ) : (
        ""
      )}
      <iframe
        id={id || policy}
        ref={iframeRef}
        style={{ ...(style || {}), border: 0 }}
        src={src + `&${sessionQueryParams.FRAME}=iframe`}
        height={height}
        width={width || "100%"}
        key={htmlKey}
        className="relative"
      />
    </div>
  );
});

OIDCFrame.displayName = "OIDCFrame";

export default OIDCFrame;
